import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { BackButton } from '@vkruglikov/react-telegram-web-app';
import { Button, useToaster, Message } from 'rsuite';

import BasicPanel from '../../components/panels/basicPanel/basicPanel';
import BasicDropdown from '../../components/inputs/basicDropdown/basicDropdown';
import BasicWrapper from '../../components/basicWrapper/basicWrapper';
import Btn from '../../components/Btn/Btn';
import { getContractsFullByID } from '../../api/auth';
import { inviteEmployee } from '../../api/employee';
import { getProfile } from '../../api/user';

import './employeeInvite.css';
import { useMessenger } from '../../context/messengerContext';
import { isIOSVersionHigherThan26Version } from '../../utils/getUserAgentParser';

const TG_BOT_NAME = process.env.REACT_APP_TG_BOT_NAME || '';
const MAX_BOT_NAME = process.env.REACT_APP_MAX_BOT_NAME || '';
const EMPLOYEES_SELECTED_CONTRACT_STORAGE_KEY = 'employees:selectedContract';

export default function EmployeeInvite() {
    const { t } = useTranslation('basic');
    const { userId, platform } = useMessenger();
    const navigate = useNavigate();
    const toaster = useToaster();

    const [contractOptions, setContractOptions] = useState([]);
    const [selectedContract, setSelectedContract] = useState(() => {
        if (typeof window === 'undefined') return '';
        return sessionStorage.getItem(EMPLOYEES_SELECTED_CONTRACT_STORAGE_KEY) ?? '';
    });
    const [contractsLoading, setContractsLoading] = useState(true);
    const [inviteLink, setInviteLink] = useState('');
    const [isLoading, setIsLoading] = useState(false);

    useEffect(() => {
        let cancelled = false;
        const run = async () => {
            setContractsLoading(true);
            try {
                const profile = await getProfile({ platform, userId });
                const contracts = await getContractsFullByID({
                    userId,
                    platform,
                });
                if (cancelled) return;

                const list = Array.isArray(contracts) ? contracts : [];

                const adminContractNumbers = new Set(
                    (Array.isArray(profile?.employees) ? profile.employees : [])
                        .filter((e) => e?.is_admin === true)
                        .map((e) => e?.client?.contract_number)
                        .filter(Boolean),
                );

                const filteredList =
                    adminContractNumbers.size > 0
                        ? list.filter((c) =>
                              adminContractNumbers.has(c?.contract_number),
                          )
                        : [];

                const toOption = (c) => ({
                    label: c.contract_number ?? '',
                    value: c.contract_number ?? '',
                });

                if (!!profile?.employees?.length) {
                    setContractOptions(filteredList.map(toOption));
                    return;
                }

                setContractOptions(list.map(toOption));
            } catch (err) {
                if (cancelled) return;
                toaster.push(
                    <Message type='error' showIcon>
                        {err?.message || t('employees_contracts_error')}
                    </Message>,
                    { placement: 'topCenter', duration: 3000 },
                );
            } finally {
                if (!cancelled) setContractsLoading(false);
            }
        };

        run();
        return () => {
            cancelled = true;
        };
    }, [platform, userId, toaster, t]);

    useEffect(() => {
        if (!selectedContract) return;
        if (!contractOptions.length) return;
        const exists = contractOptions.some((o) => o.value === selectedContract);
        if (!exists) {
            setSelectedContract('');
            if (typeof window !== 'undefined') {
                sessionStorage.removeItem(EMPLOYEES_SELECTED_CONTRACT_STORAGE_KEY);
            }
        }
    }, [contractOptions, selectedContract]);

    useEffect(() => {
        setInviteLink('');
    }, [selectedContract]);

    const handleContractChange = (value) => {
        setSelectedContract(value);
        if (typeof window !== 'undefined') {
            if (value) {
                sessionStorage.setItem(EMPLOYEES_SELECTED_CONTRACT_STORAGE_KEY, value);
            } else {
                sessionStorage.removeItem(EMPLOYEES_SELECTED_CONTRACT_STORAGE_KEY);
            }
        }
    };
    const test1 = 'https://t.me/@webant_test_cdek_bot?start=c2755c1d-7d93-44b6-8f61-1094c818ac1e'
    const test2 = 'https://max.ru/id6164143256_bot?start=c2755c1d-7d93-44b6-8f61-1094c818ac1e'

    const test3 = 'https://max.ru/id6164143256_bot?start=7179f423-b7c5-4fb9-b178-70ec3acacb74'

    const handleGetLink = async () => {
        if (!selectedContract) return;
        setIsLoading(true);
        setInviteLink('');
        try {
            const { token } = await inviteEmployee({
                contract_number: selectedContract,
            });
            let link = '';
            if (platform === 'max' && MAX_BOT_NAME && token) {
                link = `https://max.ru/${MAX_BOT_NAME}?start=${token}`;
            } else if (TG_BOT_NAME && token) {
                link = `https://t.me/${TG_BOT_NAME}?start=${token}`;
            }
            setInviteLink(link);
        } catch (err) {
            toaster.push(
                <Message type='error' showIcon>
                    {err?.message || t('employees_invite_error')}
                </Message>,
                { placement: 'topCenter', duration: 3000 },
            );
        } finally {
            setIsLoading(false);
        }
    };

    const handleCopyLink = () => {
        if (!inviteLink) return;
        const copy = () => {
            if (
                platform === 'telegram' &&
                typeof window !== 'undefined' &&
                window.Telegram?.WebApp?.copyTextToClipboard
            ) {
                window.Telegram.WebApp.copyTextToClipboard(inviteLink);
            } else if (navigator?.clipboard?.writeText) {
                navigator.clipboard.writeText(inviteLink);
            } else {
                return false;
            }
            return true;
        };
        if (copy()) {
            toaster.push(
                <Message type='success' showIcon>
                    {t('employees_invite_link_copied')}
                </Message>,
                { placement: 'topCenter', duration: 3000 },
            );
        }
    };

    const isIOS26Version = isIOSVersionHigherThan26Version();

    return (
        <div className={`stepsHeaderWrapper gradient_bg_header ${isIOS26Version ? 'ios26_top-safe-area' : ''}`}>
            <BackButton onClick={() => navigate('/employee_list')} />
            <BasicWrapper>
                <div className='employeeInvite_header'>
                    <div className='stepHeaderHeader heading-h4'>
                        {t('employees_invite_title')}
                        <img
                            id='stepsHeaderImage'
                            src='https://s3.timeweb.com/8a6e091b-publick/template_header.png'
                            alt=''
                        />
                    </div>
                </div>

                <div className='employeeInvite_selectWrap'>
                    <BasicDropdown
                        label={t('contract_number_label')}
                        placeholder={t('select_contract_number')}
                        data={contractOptions}
                        value={selectedContract}
                        onChange={handleContractChange}
                        disabled={contractsLoading}
                    />
                </div>

                <div className='employeeInvite_actions'>
                    <Button
                        className='Btn paragraph-16M'
                        appearance='primary'
                        loading={isLoading}
                        disabled={!selectedContract || contractsLoading}
                        onClick={handleGetLink}
                    >
                        {t('employees_invite_get_link')}
                    </Button>
                </div>

                {inviteLink && (
                    <BasicPanel className='employeeInvite_result'>
                        <div className='employeeInvite_linkWrap'>
                            <span className='paragraph-14R employeeInvite_linkText'>
                                {inviteLink}
                            </span>
                        </div>
                        <Btn
                            text={t('employees_invite_copy_link')}
                            fluid
                            onClick={handleCopyLink}
                        />
                    </BasicPanel>
                )}
            </BasicWrapper>
        </div>
    );
}
